import React, { useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { MapPin, Plus, Loader2, User, FileText } from 'lucide-react';

export default function AddStopDialog({ 
  open, 
  onOpenChange, 
  routeId, 
  existingStops,
  onStopAdded 
}) {
  const [adding, setAdding] = useState(false);
  const [formData, setFormData] = useState({
    defendant_name: '',
    address: '',
    case_number: '',
    documents: '',
    notes: '',
    priority: 'normal',
    service_time_minutes: 5,
    insert_after_id: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.defendant_name.trim()) {
      toast.error('Defendant name is required');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Address is required');
      return;
    }

    setAdding(true);
    try {
      const response = await api.addStopToRoute(routeId, {
        defendant_name: formData.defendant_name.trim(),
        address: formData.address.trim(),
        case_number: formData.case_number.trim() || null,
        documents: formData.documents.trim() || null,
        notes: formData.notes.trim() || null,
        priority: formData.priority,
        service_time_minutes: parseInt(formData.service_time_minutes) || 5,
        insert_after_id: formData.insert_after_id || null
      });
      
      toast.success('Stop added successfully');
      
      // Reset form
      setFormData({
        defendant_name: '',
        address: '',
        case_number: '',
        documents: '',
        notes: '',
        priority: 'normal',
        service_time_minutes: 5,
        insert_after_id: ''
      });
      
      onOpenChange(false);
      if (onStopAdded) {
        onStopAdded(response.data);
      }
    } catch (error) {
      console.error('Failed to add stop:', error);
      toast.error(error.response?.data?.detail || 'Failed to add stop');
    } finally {
      setAdding(false);
    }
  };

  // Filter to only show completed or pending stops for insertion
  const insertableStops = existingStops?.filter(s => 
    s.status === 'pending' || s.status === 'completed' || s.status === 'successful'
  ) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Stop to Route
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Defendant Name */}
          <div className="space-y-2">
            <Label className="text-slate-300 flex items-center gap-2">
              <User className="h-4 w-4" />
              Defendant Name *
            </Label>
            <Input
              value={formData.defendant_name}
              onChange={(e) => handleChange('defendant_name', e.target.value)}
              placeholder="John Smith"
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label className="text-slate-300 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Address *
            </Label>
            <Input
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="123 Main St, Austin, TX 78701"
              className="bg-slate-800 border-slate-700 text-white"
            />
            <p className="text-xs text-slate-500">
              Enter a full address - it will be geocoded automatically
            </p>
          </div>

          {/* Case Number & Documents - Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Case Number</Label>
              <Input
                value={formData.case_number}
                onChange={(e) => handleChange('case_number', e.target.value)}
                placeholder="CV-2026-12345"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Documents</Label>
              <Input
                value={formData.documents}
                onChange={(e) => handleChange('documents', e.target.value)}
                placeholder="Summons, Complaint"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>

          {/* Priority & Service Time - Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Priority</Label>
              <Select 
                value={formData.priority} 
                onValueChange={(v) => handleChange('priority', v)}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="low" className="text-white">Low</SelectItem>
                  <SelectItem value="normal" className="text-white">Normal</SelectItem>
                  <SelectItem value="high" className="text-white">High</SelectItem>
                  <SelectItem value="urgent" className="text-white">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Service Time (min)</Label>
              <Input
                type="number"
                min="1"
                max="120"
                value={formData.service_time_minutes}
                onChange={(e) => handleChange('service_time_minutes', e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>

          {/* Insert Position */}
          {insertableStops.length > 0 && (
            <div className="space-y-2">
              <Label className="text-slate-300">Insert After</Label>
              <Select 
                value={formData.insert_after_id} 
                onValueChange={(v) => handleChange('insert_after_id', v)}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="End of route (default)" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-[200px]">
                  <SelectItem value="" className="text-white">End of route (default)</SelectItem>
                  {insertableStops.map((stop, index) => (
                    <SelectItem key={stop.id} value={stop.id} className="text-white">
                      Stop {index + 1}: {stop.defendant_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Choose where to insert this stop in the route
              </p>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-slate-300 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Notes
            </Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional instructions or notes..."
              rows={2}
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="border-slate-700 text-slate-300"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={adding}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {adding ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Add Stop
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
